import { Button } from "@/components/ui/button";
import type { AccessibilityStatement } from "../types/statement";
interface SuccessScreenProps {
  setShowSuccessScreen: (show: boolean) => void;
  setCurrentStep: (step: number) => void;
  setStorage?: (
    storage:
      | AccessibilityStatement
      | ((prev: AccessibilityStatement) => AccessibilityStatement)
  ) => void;
  statement?: AccessibilityStatement;
}

export function SuccessScreen({
  setShowSuccessScreen,
  setCurrentStep,
  setStorage,
  statement,
}: SuccessScreenProps) {
  return (
    <div className="cy:p-10">
      <div className="cy:max-w-7xl cy:mx-auto cy:p-10 cy:rounded-lg" style={{ boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }}>
        {/* Header Section */}
        <div className="cy:mb-8 cy:flex cy:justify-between">
          <div className="">
            <h5 className="cy:text-base! cy:font-semibold! cy:mb-1">
              Accessibility Statement
            </h5>
            <p className="cy:text-gray-600">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          <div className="cy:flex cy:gap-4 cy:mt-4">
            <Button
              variant="outline"
              className="cy:rounded! cy:font-semibold! cy:border-primary! cy:text-primary"
              onClick={() => {
                setShowSuccessScreen(false);
                setCurrentStep(0);
                if (setStorage) {
                  setStorage((prev: AccessibilityStatement) => ({
                    ...prev,
                    status: "in_progress",
                  }));
                }
              }}
            >
              Edit statement
            </Button>
            <Button
              className="cy:rounded! cy:font-semibold! cy:bg-primary cy:text-white"
              onClick={() => {
                if (statement?.edit_url) {
                  window.open(statement.edit_url, "_blank");
                }
              }}
            >
              View Accessibility Statement
            </Button>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="cy:bg-[#F0F6FF] cy:rounded-lg cy:border cy:border-[#798DA8] cy:pt-6 cy:px-6">
          <div className="cy:grid cy:grid-cols-12 cy:gap-8">
            {/* Left Section - Instructions */}
            <div className="cy:space-y-6 cy:col-span-5">
              <div className="cy:flex cy:items-start">
                <div className="cy:rounded-full cy:flex cy:items-center cy:justify-center cy:flex-shrink-0 cy:mt-1"></div>
                <div>
                  <h2 className="cy:flex! cy:items-center cy:gap-2 cy:text-base! cy:font-semibold cy:mt-0! cy:mb-4">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.666016 8.99999C0.666016 4.39762 4.39698 0.666656 8.99935 0.666656C13.6017 0.666656 17.3327 4.39762 17.3327 8.99999C17.3327 13.6024 13.6017 17.3333 8.99935 17.3333C4.39698 17.3333 0.666016 13.6024 0.666016 8.99999ZM9.83268 6.49999V4.83332H8.16602V6.49999H9.83268ZM8.16602 7.33332V13.1667H9.83268V7.33332H8.16602Z"
                        fill="#0B66E4"
                      />
                    </svg>{" "}
                    Publish your page
                  </h2>
                  <ul className="cy:space-y-3 cy:text-sm! cy:list-disc! cy:pl-4! cy:pb-6!">
                    <li>
                      Your accessibility statement has been generated!
                    </li>
                    <li>
                      Click <strong>Edit Statement</strong> to go back and make
                      changes.
                    </li>
                    <li>
                      Click <strong>View Accessibility Statement</strong> to
                      open the draft page. From there, review and hit{" "}
                      <strong>Publish</strong> to make it live.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Section - Browser Illustration */}
            <div className="cy:flex cy:justify-end cy:col-span-7 cy:items-end">
              <svg
                width="381"
                height="189"
                viewBox="0 0 381 189"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_692_1786)">
                  <rect
                    x="0.5"
                    y="0.5"
                    width="379.5"
                    height="219"
                    rx="5.5"
                    fill="white"
                    stroke="#1869E3"
                  />
                  <g clip-path="url(#clip1_692_1786)">
                    <path
                      d="M13.1778 0H367.823C374.267 0 379.5 4.78413 379.5 10.6753V189.411C379.5 195.302 374.267 200.086 367.823 200.086H12.8442C6.0311 200.086 0.5 195.03 0.5 188.801V11.5904C0.5 5.19364 6.18092 0 13.1778 0Z"
                      fill="#7C95A5"
                    />
                    <path
                      d="M0.833627 0H379.5V220.078H0.5V0.335485C0.5 0.150658 0.649823 0 0.833627 0Z"
                      fill="#1578F7"
                    />
                    <path
                      d="M379.5 35.8364H0.5V219H379.5V35.8364Z"
                      fill="white"
                    />
                    <path
                      d="M102.754 22.813C100.447 22.813 98.577 20.9321 98.5801 18.6116C98.5847 14.8126 98.5863 9.81292 98.5863 9.81292C98.5863 6.24684 95.7103 3.35483 92.164 3.35483H32.9452C29.3989 3.35483 26.5229 6.24684 26.5229 9.81292C26.5229 9.81292 26.5244 13.8605 26.5275 17.439C26.5306 20.4071 24.1381 22.813 21.188 22.813H0.5V47.6389H379.5V22.813H102.754Z"
                      fill="white"
                    />
                    <path
                      d="M16.9579 35.1701C16.9518 35.4667 16.7077 35.7044 16.4127 35.6981L5.47254 35.4916C5.17752 35.4854 4.94121 35.24 4.94738 34.9433C4.95356 34.6466 5.1976 34.409 5.49262 34.4152L16.4328 34.6218C16.7278 34.628 16.9641 34.8734 16.9579 35.1701Z"
                      fill="#D7D9E5"
                    />
                    <path
                      d="M5.03202 34.8202L5.15395 34.684C5.28001 34.5432 5.49493 34.5329 5.63384 34.6587L9.60599 38.2538C9.74604 38.3806 9.75625 38.5967 9.63122 38.7364L9.50929 38.8726C9.38322 39.0135 9.1683 39.0237 9.02939 38.898L5.05725 35.3028C4.91719 35.176 4.90699 34.9599 5.03202 34.8202Z"
                      fill="#D7D9E5"
                    />
                    <path
                      d="M9.77579 31.3311L9.65906 31.1904C9.53885 31.0455 9.32457 31.026 9.18045 31.1468L5.07638 34.5889C4.93226 34.7098 4.91287 34.9253 5.03308 35.0702L5.14981 35.211C5.27002 35.3559 5.4843 35.3754 5.62842 35.2545L9.73249 31.8124C9.87661 31.6915 9.896 31.4761 9.77579 31.3311Z"
                      fill="#D7D9E5"
                    />
                    <path
                      d="M22.965 35.1701C22.9711 35.4668 23.2152 35.7044 23.5102 35.6982L34.4504 35.4916C34.7454 35.4854 34.9817 35.24 34.9755 34.9434C34.9693 34.6467 34.7253 34.4091 34.4303 34.4153L23.4901 34.6219C23.1951 34.6281 22.9588 34.8735 22.965 35.1701Z"
                      fill="#D7D9E5"
                    />
                    <path
                      d="M30.4133 38.8701L30.2914 38.7339C30.1654 38.593 30.1778 38.377 30.3167 38.2513L34.2895 34.6568C34.4296 34.5301 34.6444 34.5426 34.7694 34.6823L34.8913 34.8185C35.0174 34.9594 35.0049 35.1754 34.866 35.3011L30.8932 38.8956C30.7531 39.0223 30.5383 39.0098 30.4133 38.8701Z"
                      fill="#D7D9E5"
                    />
                    <path
                      d="M34.7808 35.2118L34.8976 35.0711C35.0178 34.9261 34.9984 34.7107 34.8543 34.5898L30.7502 31.1477C30.6061 31.0268 30.3918 31.0463 30.2716 31.1912L30.1549 31.332C30.0347 31.4769 30.054 31.6924 30.1982 31.8133L34.3022 35.2554C34.4464 35.3762 34.6606 35.3567 34.7808 35.2118Z"
                      fill="#D7D9E5"
                    />
                    <path
                      d="M350.879 25.9675H55.6505C50.6756 25.9675 46.6426 30.023 46.6426 35.0256C46.6426 40.0283 50.6756 44.0837 55.6505 44.0837H350.879C355.854 44.0837 359.887 40.0283 359.887 35.0256C359.887 30.023 355.854 25.9675 350.879 25.9675Z"
                      fill="#EFF2FF"
                    />
                    <path
                      d="M370.324 32.9831H366.709C366 32.9831 365.426 32.4053 365.426 31.6924C365.426 30.9795 366 30.4017 366.709 30.4017H370.324C371.033 30.4017 371.607 30.9795 371.607 31.6924C371.607 32.4053 371.033 32.9831 370.324 32.9831Z"
                      fill="#CFD3E8"
                    />
                    <path
                      d="M370.324 36.3162H366.709C366 36.3162 365.426 35.7385 365.426 35.0255C365.426 34.3126 366 33.7349 366.709 33.7349H370.324C371.033 33.7349 371.607 34.3126 371.607 35.0255C371.607 35.7385 371.033 36.3162 370.324 36.3162Z"
                      fill="#CFD3E8"
                    />
                    <path
                      d="M370.324 39.6477H366.709C366 39.6477 365.426 39.0699 365.426 38.357C365.426 37.6441 366 37.0663 366.709 37.0663H370.324C371.033 37.0663 371.607 37.6441 371.607 38.357C371.607 39.0699 371.033 39.6477 370.324 39.6477Z"
                      fill="#CFD3E8"
                    />
                    <path
                      d="M59.2008 30.9097C57.8493 29.5507 55.6483 29.5507 54.2968 30.9097C52.9453 32.2687 52.9453 34.482 54.2968 35.841C55.4923 37.0432 57.3489 37.1814 58.6988 36.2588L62.3888 39.9694C62.6421 40.2241 63.053 40.2241 63.3063 39.9694C63.5596 39.7147 63.5596 39.3015 63.3063 39.0468L59.6163 35.3363C60.5338 33.9803 60.3963 32.1119 59.2008 30.9097ZM58.2834 34.92C57.4369 35.7711 56.0607 35.7711 55.2143 34.92C54.3679 34.0689 54.3679 32.685 55.2143 31.8339C56.0607 30.9827 57.4369 30.9827 58.2834 31.8339C59.1298 32.685 59.1298 34.0689 58.2834 34.92Z"
                      fill="#CFD3E8"
                    />
                    <path
                      opacity="0.2"
                      d="M340.669 77.4766H215.295C214.407 77.4766 213.688 78.8204 213.688 80.478V196.866C213.688 198.523 214.407 199.867 215.295 199.867H340.669C341.557 199.867 342.277 198.523 342.277 196.866V80.478C342.277 78.8204 341.557 77.4766 340.669 77.4766Z"
                      fill="#F2F3F8"
                    />
                    <g clip-path="url(#clip2_692_1786)">
                      <rect
                        width="128.227"
                        height="122.886"
                        transform="translate(213.688 95.8303)"
                        fill="#F2F3F8"
                      />
                      <path
                        opacity="0.2"
                        d="M328.479 116.274C361.278 116.274 377.257 142.081 409.216 176.717C383.145 176.717 271.53 176.717 239.33 176.717C267.084 148.193 295.678 116.274 328.479 116.274Z"
                        fill="#D3DAFB"
                      />
                      <path
                        opacity="0.2"
                        d="M219.303 106.659C254.362 106.659 277.045 140.064 308.39 176.717C267.263 176.717 167.665 176.717 130.217 176.717C160.494 140.064 184.245 106.659 219.303 106.659Z"
                        fill="#CDD9FF"
                      />
                      <path
                        opacity="0.5"
                        d="M278.001 106.891C284.867 106.891 290.432 101.326 290.432 94.4604C290.432 87.5952 284.867 82.0297 278.001 82.0297C271.136 82.0297 265.57 87.5952 265.57 94.4604C265.57 101.326 271.136 106.891 278.001 106.891Z"
                        fill="#D3DAFB"
                      />
                    </g>
                    <path
                      opacity="0.5"
                      d="M189.752 125.759H34.5847C33.2025 125.759 32.082 126.764 32.082 128.005C32.082 129.245 33.2025 130.25 34.5847 130.25H189.752C191.134 130.25 192.255 129.245 192.255 128.005C192.255 126.764 191.134 125.759 189.752 125.759Z"
                      fill="#E3E8FF"
                    />
                    <path
                      d="M74.6945 160.567H34.5886C33.2042 160.567 32.082 161.657 32.082 163V172.732C32.082 174.075 33.2042 175.164 34.5886 175.164H74.6945C76.0789 175.164 77.2011 174.075 77.2011 172.732V163C77.2011 161.657 76.0789 160.567 74.6945 160.567Z"
                      fill="#E3E8FF"
                    />
                    <path
                      d="M114.192 115.548H34.5702C33.196 115.548 32.082 116.679 32.082 118.074V123.127C32.082 124.522 33.196 125.653 34.5702 125.653H114.192C115.566 125.653 116.68 124.522 116.68 123.127V118.074C116.68 116.679 115.566 115.548 114.192 115.548Z"
                      fill="#E3E8FF"
                    />
                    <circle
                      cx="362.016"
                      cy="12.9717"
                      r="3.94792"
                      fill="white"
                    />
                    <circle
                      cx="349.608"
                      cy="12.9717"
                      r="3.94792"
                      fill="white"
                    />
                    <circle
                      cx="337.202"
                      cy="12.9717"
                      r="3.94792"
                      fill="white"
                    />
                    <line
                      x1="0.5"
                      y1="46.811"
                      x2="379.5"
                      y2="46.811"
                      stroke="#F2F3F8"
                      stroke-width="1.12798"
                    />
                    <rect
                      x="290"
                      y="55"
                      width="63"
                      height="24"
                      rx="2"
                      fill="#219653"
                    />
                    <path
                      d="M301.959 71V62.2727H305.07C305.749 62.2727 306.311 62.3963 306.757 62.6435C307.203 62.8906 307.537 63.2287 307.759 63.6577C307.98 64.0838 308.091 64.5639 308.091 65.098C308.091 65.6349 307.979 66.1179 307.754 66.5469C307.533 66.973 307.197 67.3111 306.749 67.5611C306.303 67.8082 305.741 67.9318 305.065 67.9318H302.926V66.8153H304.946C305.375 66.8153 305.723 66.7415 305.99 66.5938C306.257 66.4432 306.453 66.2386 306.578 65.9801C306.703 65.7216 306.766 65.4276 306.766 65.098C306.766 64.7685 306.703 64.4759 306.578 64.2202C306.453 63.9645 306.256 63.7642 305.986 63.6193C305.719 63.4744 305.366 63.402 304.929 63.402H303.276V71H301.959ZM313.439 68.2855V64.4545H314.717V71H313.464V69.8665H313.396C313.245 70.2159 313.004 70.5071 312.672 70.7401C312.342 70.9702 311.932 71.0852 311.44 71.0852C311.02 71.0852 310.647 70.9929 310.324 70.8082C310.003 70.6207 309.75 70.3438 309.565 69.9773C309.383 69.6108 309.292 69.1577 309.292 68.6179V64.4545H310.566V68.4645C310.566 68.9105 310.69 69.2656 310.937 69.5298C311.184 69.794 311.505 69.9261 311.9 69.9261C312.139 69.9261 312.376 69.8665 312.612 69.7472C312.851 69.6278 313.048 69.4474 313.204 69.206C313.363 68.9645 313.441 68.6577 313.439 68.2855ZM316.291 71V62.2727H317.565V65.5156H317.642C317.716 65.3793 317.823 65.2216 317.962 65.0426C318.101 64.8636 318.294 64.7074 318.541 64.5739C318.788 64.4375 319.115 64.3693 319.521 64.3693C320.05 64.3693 320.521 64.5028 320.936 64.7699C321.351 65.0369 321.676 65.4219 321.912 65.9247C322.151 66.4276 322.27 67.0327 322.27 67.7401C322.27 68.4474 322.152 69.054 321.916 69.5597C321.681 70.0625 321.357 70.4503 320.945 70.723C320.533 70.9929 320.063 71.1278 319.534 71.1278C319.136 71.1278 318.811 71.0611 318.558 70.9276C318.308 70.794 318.112 70.6378 317.97 70.4588C317.828 70.2798 317.719 70.1207 317.642 69.9815H317.536V71H316.291ZM317.54 67.7273C317.54 68.1875 317.607 68.5909 317.74 68.9375C317.874 69.2841 318.067 69.5554 318.32 69.7514C318.573 69.9446 318.882 70.0412 319.249 70.0412C319.629 70.0412 319.948 69.9403 320.203 69.7386C320.459 69.5341 320.652 69.2571 320.783 68.9077C320.916 68.5582 320.983 68.1648 320.983 67.7273C320.983 67.2955 320.918 66.9077 320.787 66.5639C320.659 66.2202 320.466 65.9489 320.208 65.75C319.952 65.5511 319.632 65.4517 319.249 65.4517C318.879 65.4517 318.567 65.5469 318.311 65.7372C318.058 65.9276 317.867 66.1932 317.736 66.5341C317.605 66.875 317.54 67.2727 317.54 67.7273ZM324.735 62.2727V71H323.461V62.2727H324.735ZM326.209 71V64.4545H327.483V71H326.209ZM326.853 63.4446C326.631 63.4446 326.441 63.3707 326.281 63.223C326.125 63.0724 326.047 62.8935 326.047 62.6861C326.047 62.4759 326.125 62.2969 326.281 62.1491C326.441 61.9986 326.631 61.9233 326.853 61.9233C327.074 61.9233 327.263 61.9986 327.419 62.1491C327.578 62.2969 327.658 62.4759 327.658 62.6861C327.658 62.8935 327.578 63.0724 327.419 63.223C327.263 63.3707 327.074 63.4446 326.853 63.4446ZM333.862 66.0526L332.707 66.2571C332.659 66.1094 332.582 65.9687 332.477 65.8352C332.375 65.7017 332.236 65.5923 332.06 65.5071C331.883 65.4219 331.663 65.3793 331.399 65.3793C331.038 65.3793 330.737 65.4602 330.496 65.6222C330.254 65.7812 330.133 65.9872 330.133 66.2401C330.133 66.4588 330.214 66.6349 330.376 66.7685C330.538 66.902 330.8 67.0114 331.16 67.0966L332.2 67.3352C332.802 67.4744 333.251 67.6889 333.547 67.9787C333.842 68.2685 333.99 68.6449 333.99 69.108C333.99 69.5 333.876 69.8494 333.649 70.1562C333.425 70.4602 333.111 70.6989 332.707 70.8722C332.307 71.0455 331.842 71.1321 331.314 71.1321C330.581 71.1321 329.983 70.9759 329.52 70.6634C329.057 70.348 328.773 69.9006 328.668 69.321L329.899 69.1335C329.976 69.4545 330.133 69.6974 330.372 69.8622C330.611 70.0241 330.922 70.1051 331.305 70.1051C331.723 70.1051 332.057 70.0185 332.307 69.8452C332.557 69.669 332.682 69.4545 332.682 69.2017C332.682 68.9972 332.605 68.8253 332.452 68.6861C332.301 68.5469 332.07 68.4418 331.757 68.3707L330.649 68.1278C330.038 67.9886 329.587 67.767 329.294 67.4631C329.004 67.1591 328.859 66.7741 328.859 66.3082C328.859 65.9219 328.967 65.5838 329.183 65.294C329.399 65.0043 329.697 64.7784 330.078 64.6165C330.459 64.4517 330.895 64.3693 331.386 64.3693C332.094 64.3693 332.65 64.5227 333.057 64.8295C333.463 65.1335 333.731 65.5412 333.862 66.0526ZM336.425 67.1136V71H335.151V62.2727H336.408V65.5199H336.489C336.642 65.1676 336.877 64.8878 337.192 64.6804C337.507 64.473 337.919 64.3693 338.428 64.3693C338.877 64.3693 339.269 64.4616 339.604 64.6463C339.942 64.831 340.203 65.1065 340.388 65.473C340.576 65.8366 340.669 66.2912 340.669 66.8366V71H339.395V66.9901C339.395 66.5099 339.272 66.1378 339.024 65.8736C338.777 65.6065 338.434 65.473 337.993 65.473C337.692 65.473 337.422 65.5369 337.184 65.6648C336.948 65.7926 336.762 65.9801 336.625 66.2273C336.492 66.4716 336.425 66.767 336.425 67.1136Z"
                      fill="white"
                    />
                    <rect
                      x="34.3398"
                      y="11.2797"
                      width="45.119"
                      height="3.38393"
                      fill="#EBEEF9"
                    />
                  </g>
                  <rect
                    x="0.75"
                    y="0.25"
                    width="378.5"
                    height="218.5"
                    rx="4.75"
                    stroke="#1578F7"
                    stroke-width="0.5"
                  />
                  <g filter="url(#filter0_d_692_1786)">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M357.841 79.5161L362.487 77.8649L350 71L353.64 84.7764L356.366 80.668L359.63 84.8454L361.104 83.6935L357.841 79.5161Z"
                      fill="white"
                    />
                    <path
                      d="M350.289 70.4746L362.775 77.3389L363.946 77.9824L362.688 78.4307L358.825 79.8018L361.577 83.3242L361.946 83.7969L361.474 84.166L359.999 85.3184L359.526 85.6875L359.157 85.2148L356.405 81.6934L354.141 85.1084L353.401 86.2217L353.061 84.9297L349.42 71.1533L349.062 69.7998L350.289 70.4746Z"
                      stroke="#363B3E"
                      stroke-width="1.2"
                    />
                  </g>
                </g>
                <defs>
                  <filter
                    id="filter0_d_692_1786"
                    x="345.125"
                    y="66.5995"
                    width="23.2812"
                    height="25.067"
                    filterUnits="userSpaceOnUse"
                    color-interpolation-filters="sRGB"
                  >
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="1" />
                    <feGaussianBlur stdDeviation="1.5" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_692_1786"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_692_1786"
                      result="shape"
                    />
                  </filter>
                  <clipPath id="clip0_692_1786">
                    <rect width="380.5" height="220" fill="white" />
                  </clipPath>
                  <clipPath id="clip1_692_1786">
                    <rect
                      x="0.5"
                      width="379"
                      height="219"
                      rx="5"
                      fill="white"
                    />
                  </clipPath>
                  <clipPath id="clip2_692_1786">
                    <rect
                      width="128.227"
                      height="122.886"
                      fill="white"
                      transform="translate(213.688 95.8303)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
